import _ from 'lodash'
import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import { userEmailOption } from 'src/components/UserDropdown/UserDropdown'
import { MetricsSeries } from 'src/models/spendMetrics.models'
import {
  AccountOverviewMatchParams,
  accountRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsSnapshotPaginatedDataState } from 'src/redux/creditCardsSnapshotPaginatedData.reducer'
import { RecentUsersFullPaginatedDataState } from 'src/redux/recentUsersFullPaginatedData.reducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardListModelsFromState,
  selectCreditCardPermissionsModelsFromState,
} from 'src/redux/selectors/data/creditCards.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectVirtualCardListModels } from 'src/redux/selectors/data/virtualCards.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { VirtualCardsSnapshotPaginatedDataState } from 'src/redux/virtualCardsSnapshotPaginatedData.reducer'
import { toDollars } from 'src/utils/currency.utils'

export const selectCreditCardIdFromRoute = createSelector(
  [selectCalculatedBackgroundLocation],
  (backgroundLocation) => {
    const mtch = matchPath<AccountOverviewMatchParams>(
      backgroundLocation.pathname,
      accountRouteProps,
    ) as match<AccountOverviewMatchParams>
    return mtch.params.id
  },
)

export const selectCreditCardPermissions = createSelector(
  [selectCreditCardPermissionsModelsFromState, selectCreditCardIdFromRoute],
  (creditCardPermissions, creditCardId) => {
    return creditCardPermissions[creditCardId] !== undefined
      ? creditCardPermissions[creditCardId]
      : undefined
  },
)

export const selectCreditCard = createSelector(
  [selectCreditCardListModelsFromState, selectCreditCardIdFromRoute],
  (creditCards, creditCardId) => {
    return creditCards[creditCardId] !== undefined
      ? creditCards[creditCardId]
      : undefined
  },
)

export const selectCreditCardParent = createSelector(
  [selectCreditCard, selectCreditCardListModelsFromState],
  (creditCard, creditCards) => {
    return creditCard?.parentCreditCardId !== undefined
      ? creditCards[creditCard.parentCreditCardId]
      : undefined
  },
)

export const selectParentCreditCardPermissions = createSelector(
  [selectCreditCard, selectCreditCardPermissionsModelsFromState],
  (creditCard, parentCreditCardPermissions) =>
    creditCard !== undefined &&
    creditCard.parentCreditCardId !== undefined &&
    parentCreditCardPermissions[creditCard?.parentCreditCardId] !== undefined
      ? parentCreditCardPermissions[creditCard.parentCreditCardId]
      : undefined,
)

export const selectCreditCardOwner = createSelector(
  [selectCreditCard, selectUserListModelsFromState],
  (creditCard, users) =>
    creditCard !== undefined ? users[creditCard.ownerId] : undefined,
)

export const selectCreditCardUser = createSelector(
  [selectCreditCard, selectUserListModelsFromState],
  (creditCard, users) =>
    creditCard !== undefined ? users[creditCard.userId] : undefined,
)

export const selectCreditCardUsers = createSelector(
  [selectCreditCard],
  (creditCard) => creditCard?.budgetUsers,
)

const selectDataSetsFromState = (state: RootState): number[] | undefined =>
  state.app.account.accountOverview.dataSets
const selectSeriesFromState = (state: RootState): MetricsSeries[] | undefined =>
  state.app.account.accountOverview.series
export const selectSpendMetricsLoadingFromState = (state: RootState): boolean =>
  state.app.account.accountOverview.spendMetricsLoading
export const selectGraphData = createSelector(
  [selectDataSetsFromState, selectSeriesFromState],
  (dataSets, series) =>
    dataSets !== undefined && series !== undefined
      ? dataSets.map((date, i) => ({
          x: date,
          y: toDollars(series[0].data[i]),
        }))
      : [],
)
export const selectTotalSpent = createSelector(
  [selectSeriesFromState],
  (series) =>
    series !== undefined && series[0] !== undefined
      ? series[0].data.reduce((acc, curr) => acc + curr, 0)
      : undefined,
)

export const selectVirtualCardsSnapshotPaginatedData = (
  state: RootState,
): VirtualCardsSnapshotPaginatedDataState =>
  state.app.account.accountOverview.virtualCardsSnapshotPaginatedData
const selectUniqueVirtualCardIds = createSelector(
  [selectVirtualCardsSnapshotPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueVirtualCardListModels = createSelector(
  [selectUniqueVirtualCardIds, selectVirtualCardListModels],
  (ids, virtualCards) => ids.map((id) => virtualCards[id]),
)
export const selectFilteredVirtualCards = createSelector(
  [selectUniqueVirtualCardListModels, selectCreditCardIdFromRoute],
  (virtualCards, creditCardId) =>
    virtualCards.filter(
      (vc) => vc.isActive && vc.creditCardId === creditCardId,
    ),
)

export const selectCreditCardsSnapshotPaginatedData = (
  state: RootState,
): CreditCardsSnapshotPaginatedDataState =>
  state.app.account.accountOverview.creditCardsSnapshotPaginatedData
const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsSnapshotPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueCreditCardListModels = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCards[id]),
)
export const selectFilteredCreditCards = createSelector(
  [selectUniqueCreditCardListModels, selectCreditCardIdFromRoute],
  (creditCards, id) =>
    creditCards.filter((cc) => cc.isBudget && cc.parentCreditCardId === id),
)

export const selectPostingAccountVerification = (
  state: RootState,
): boolean | undefined =>
  state.app.account.accountOverview.postingAccountVerification

export const selectAccountVerificationError = (
  state: RootState,
): string | undefined =>
  state.app.account.accountOverview.accountVerificationError

export const selectPostingResendAccountVerification = (
  state: RootState,
): boolean | undefined =>
  state.app.account.accountOverview.postingResendAccountVerification

export const selectAccountResendVerificationError = (
  state: RootState,
): string | undefined =>
  state.app.account.accountOverview.accountResendVerificationError

export const selectFetchingCreditCards = (
  state: RootState,
): boolean | undefined => state.app.account.accountOverview.fetchingCreditCards

export const selectFetchingVirtualCards = (
  state: RootState,
): boolean | undefined => state.app.account.accountOverview.fetchingVirtualCards
export const selectUsersFullPaginatedData = (
  state: RootState,
): UsersFullPaginatedDataState =>
  state.app.account.accountOverview.usersFullPaginatedData

export const selectUpdateBudgetError = (state: RootState): string | undefined =>
  state.app.account.accountOverview.updateBudgetError

export const selectRemovingBudgetUser = (state: RootState): boolean =>
  state.app.account.accountOverview.removingBudgetUser

export const selectRemovingBudgetUserTopLevelError = (
  state: RootState,
): string | undefined =>
  state.app.account.accountOverview.removingBudgetUserTopLevelError

const selectUniqueUserIds = createSelector(
  [selectUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)

const selectUniqueUserEmailOptions = createSelector(
  [selectUniqueUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)

export const selectRecentUsersFullPaginatedData = (
  state: RootState,
): RecentUsersFullPaginatedDataState | undefined =>
  state.app.account.accountOverview.recentUsersFullPaginatedData

const selectUniqueRecentUserIds = createSelector(
  [selectRecentUsersFullPaginatedData],
  (data) => data?.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserIds, selectUserListModelsFromState],
  (ids, users) => ids?.map((id) => userEmailOption(users[id])),
)

export const selectFilteredRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserEmailOptions, selectUserListModelFromState],
  (options, user) => {
    const ownUser = options?.find(
      (option) => option.details?.user?.id === user?.id,
    )
    const filteredOptions = options?.filter(
      (option) =>
        option.details?.user?.id !== user?.id &&
        _.get(option.details, 'user.id', undefined) !== user?.id &&
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === option.details?.user?.organizationId),
    )
    if (ownUser) {
      filteredOptions?.unshift(ownUser)
    }
    return filteredOptions?.slice(0, 5)
  },
)

export const selectFilteredUserEmailOptions = createSelector(
  [
    selectUniqueUserEmailOptions,
    selectUserListModelFromState,
    selectFilteredRecentUserEmailOptions,
  ],
  (options, user, suggested) => {
    return options.filter(
      (option) =>
        // TODO: remove commented code once AC is decided
        // include unverified users
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === option.details?.user?.organizationId) &&
        suggested
          ?.map((suggestion) => suggestion.value)
          .indexOf(option.value) === -1,
      // exclude unverified users
      // (user?.hasOrgAdminPermissions || user?.organizationId === person.details.user?.organizationId) && option.details.user.verified && suggested.map((suggestion) => suggestion.value).indexOf(option.value) === -1,
    )
  },
)

export const selectFilteredUserEmailOptionsWithoutBudgetUsers = createSelector(
  [
    selectFilteredUserEmailOptions,
    selectUserListModelFromState,
    selectCreditCardUsers,
  ],
  (filteredEmails, user, budgetUsers) =>
    filteredEmails.filter(
      (person) =>
        // TODO: remove commented code once AC is decided
        // include unverified users
        // (user?.hasOrgAdminPermissions || user?.organizationId === person.details.user?.organizationId) && budgetUsers.map((budgetUser) => budgetUser.email).indexOf(person.value) === -1
        // exclude unverified users
        (user?.hasOrgAdminPermissions ||
          user?.organizationId === person.details?.user?.organizationId) &&
        person.details?.user.verified &&
        budgetUsers
          ?.map((budgetUser) => budgetUser.email)
          .indexOf(person.value) === -1,
    ),
)

export const selectFilteredRecentUserEmailOptionsWithoutBudgetUsers =
  createSelector(
    [
      selectFilteredRecentUserEmailOptions,
      selectUserListModelFromState,
      selectCreditCardUsers,
    ],
    (filteredRecentEmails, user, budgetUsers) =>
      filteredRecentEmails?.filter(
        (person) =>
          // TODO: remove commented code once AC is decided
          // include unverified users
          // (user?.hasOrgAdminPermissions || user?.organizationId === person.details.user?.organizationId) && budgetUsers.map((budgetUser) => budgetUser.email).indexOf(person.value) === -1
          // exclude unverified users
          (user?.hasOrgAdminPermissions ||
            user?.organizationId === person.details?.user?.organizationId) &&
          person.details?.user.verified &&
          budgetUsers
            ?.map((budgetUser) => budgetUser.email)
            .indexOf(person.value) === -1,
      ),
  )
